import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ImageSliderWrapper from './ImageSlider.styles';
import {PlaceholderWrapper, CarouselControls} from './ImageSlider.styles';
import { isEditorActive, Image, Placeholder, withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';
import ImageSliderSlide from '../ImageSliderSlide/ImageSliderSlide';
import Button from '../shared/Button/Button.jsx';


const ImageSlider = ({ sitecoreContext, fields, rendering, params = {} }) => {
  const experienceEditor = isEditorActive();
  const { title, cornerGraphic } = fields;
  const {
    componentclass = '',
    id = '',
    dots = false,
    arrows = false,
    autoplay = true,
    pauseOnHover = true,
    autoplaySpeed = 4000,
    backgroundHex = '#592A03',
    backgroundColor = null,
  } = params;

  const sliderSettings = {
    dots,
    arrows,
    autoplay,
    autoplaySpeed,
    pauseOnHover,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
  };
  const carouselItems = rendering?.placeholders?.['image-slide'] || [];
  const slides = Array.isArray(carouselItems)
    ? carouselItems.map((item, index) => ({ ...item, slideIndex: index }))
    : [];
  const sliderRef = useRef();
  const prevButtonRef = useRef();
  const nextButtonRef = useRef();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slidesCount = slides.length;

  // Update button disabled state on slide change
  useEffect(() => {
    if (prevButtonRef.current) {
      if (currentSlide === 0) {
        prevButtonRef.current.classList.add('slick-disabled');
      } else {
        prevButtonRef.current.classList.remove('slick-disabled');
      }
    }
    if (nextButtonRef.current) {
      if (currentSlide === slidesCount - 1) {
        nextButtonRef.current.classList.add('slick-disabled');
      } else {
        nextButtonRef.current.classList.remove('slick-disabled');
      }
    }
  }, [currentSlide, slidesCount]);

  const next = useCallback(() => {
    sliderRef.current.slickNext();
  }, []);

  const previous = useCallback(() => {
    sliderRef.current.slickPrev();
  }, []);

  if (experienceEditor || sitecoreContext?.pageEditing) {
    return (
      <ImageSliderWrapper
        className={componentclass}
        id={id}
        experienceEditor={experienceEditor || sitecoreContext?.pageEditing}
        backgroundColor={backgroundHex ? undefined : backgroundColor}
        backgroundHex={backgroundHex}>
        {title && (
            <Text tag="h1" field={fields?.title} />
        )}
        <PlaceholderWrapper style={{ minHeight: '300px', padding: '50px 100px', display: 'block', position: 'relative' }}>
          <h2>Image Slider Slides</h2>
          <Placeholder name="image-slide" rendering={rendering} style={{ minHeight: '300px', width: '100%', display: 'block', position: 'relative' }} />
        </PlaceholderWrapper>
      </ImageSliderWrapper>
    );
  }else{
    return (
      <ImageSliderWrapper
        className={componentclass}
        id={id}
        backgroundColor={backgroundHex ? undefined : backgroundColor}
        backgroundHex={backgroundHex}
      >
        <div className="imageSlider-inner">
          {title && (
            <Text className="title-text" tag="h2" field={fields?.title} />
          )}
          {cornerGraphic?.value?.src && (
            <Image field={cornerGraphic} className="corner-graphic" />
          )}
          <Slider
            {...sliderSettings}
            ref={sliderRef}
            beforeChange={(_, newIndex) => setCurrentSlide(newIndex)}
          >
            {slides.map((item) => (
              <ImageSliderSlide key={item.slideIndex} rendering={{...item}} />
            ))}
          </Slider>
        </div>
        {slides.length > 1 && (
          <CarouselControls className="carousel-controls">
            <Button
              type="circle"
              icon="chevron-left"
              variant="transparentYellow"
              size="xlarge"
              ariaLabel="Previous slide"
              className="imageSlider-prev"
              onClick={previous}
              ref={prevButtonRef}
            />
            <Button
              type="circle"
              icon="chevron-right"
              variant="transparentYellow"
              size="xlarge"
              ariaLabel="Next slide"
              className="imageSlider-next"
              onClick={next}
              ref={nextButtonRef}
            />
          </CarouselControls>
        )}
      </ImageSliderWrapper>
    );
  }
};

ImageSlider.propTypes = {
  params: PropTypes.object,
  rendering: PropTypes.object,
  sitecoreContext: PropTypes.object,
};

export default withSitecoreContext()(ImageSlider);